import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LocalizePipe } from '@wonder-core-ng/localize.pipe';
import { L10nTranslationModule, L10nTranslationService } from 'angular-l10n';
import { IDashboard } from '../interfaces/dashboard.interface';

export type DashboardMarkerKind = 'draft' | 'restricted' | null;

/**
 * What a menu entry or a list row says about a dashboard's visibility (WB-185): a «Borrador» chip while
 * it is a draft, a lock when it is published for chosen people only, nothing otherwise.
 * The lock's tooltip names the roles and people the dashboard is restricted to when the dashboard carries them
 * (the server sends them only to who may change visibility); otherwise it just says it is restricted.
 * Standalone and store-free so the tenants' menus can import it by its deep path.
 */
@Component({
  selector: 'wbi-dashboard-status-marker',
  imports: [MatIconModule, MatTooltipModule, L10nTranslationModule],
  template: `
    @switch (kind()) {
      @case ('draft') {
        <span class="dsm-draft">{{ '$dashboard-status.draft' | translateAsync }}</span>
      }
      @case ('restricted') {
        <mat-icon
          class="dsm-restricted"
          role="img"
          [matTooltip]="restrictedText()"
          [attr.aria-label]="restrictedText()"
          >lock</mat-icon
        >
      }
    }
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      flex: none;
    }
    .dsm-draft {
      padding: 1px 8px;
      border-radius: 999px;
      background: var(--mat-sys-tertiary-container);
      color: var(--mat-sys-on-tertiary-container);
      font: var(--mat-sys-label-small);
      white-space: nowrap;
    }
    .dsm-restricted {
      width: 18px;
      height: 18px;
      font-size: 18px;
      color: var(--mat-sys-on-surface-variant);
    }
  `,
  providers: [LocalizePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardStatusMarkerComponent {
  private readonly translation = inject(L10nTranslationService);
  // The text is built here, so it has to be rebuilt when the language changes.
  private readonly language = toSignal(this.translation.onChange());
  private readonly localizer = inject(LocalizePipe);

  readonly dashboard = input<
    Pick<IDashboard, 'status' | 'visibility' | 'visibility_roles' | 'visibility_users'> | null | undefined
  >();
  protected readonly kind = computed<DashboardMarkerKind>(() => {
    const dashboard = this.dashboard();
    if (dashboard?.status === 'draft') return 'draft';
    return dashboard?.status === 'published' && dashboard.visibility === 'restricted' ? 'restricted' : null;
  });

  /** «Visibilidad restringida», followed by who sees it when the dashboard carries its roles and people. */
  protected readonly restrictedText = computed(() => {
    this.language();
    const t = (key: string, params?: object) => this.translation.translate(`$dashboard-status.${key}`, params);
    const { visibility_roles: roles, visibility_users: users } = this.dashboard() ?? {};
    if (!roles && !users) return t('restricted');
    const lang = this.translation.getLocale()?.language || 'en-GB';
    const roleNames = (roles ?? []).map((role) => this.localizer.transformToLocale(role.name, lang));
    const people = (users ?? []).map((user) => [user.name, user.surname].filter(Boolean).join(' ') || user.email);
    const who = [
      roleNames.length ? t('restricted-roles', { list: roleNames.join(', ') }) : '',
      people.length ? t('restricted-users', { list: people.join(', ') }) : '',
    ].filter(Boolean);
    return [t('restricted') + '.', ...(who.length ? who : [t('restricted-nobody')])].join(' ');
  });
}
